/**
 * The history tabs of a customer page (`/users/$userId`), in the strip's order. The tab rides the
 * URL (`?tab=`), so a tab is a link. The Shopify tab is a Shopify customer's alone, and Live breaks
 * shows only while the tenant has the module; the two screening tabs keep short labels so the
 * strip fits on one line.
 */
export const USER_TABS = [
  "purchases",
  "ledger",
  "opens",
  "inventory",
  "orders",
  "battles",
  "liveBreaks",
  "fraud",
  "kyc",
  "shopify",
] as const;
export type UserTab = (typeof USER_TABS)[number];

export const USER_TAB_LABELS: Record<UserTab, string> = {
  purchases: "Purchases",
  ledger: "Ledger",
  opens: "Opens",
  inventory: "Inventory",
  orders: "Orders",
  battles: "Battles",
  liveBreaks: "Live breaks",
  fraud: "Fraud",
  kyc: "Identity",
  shopify: "Shopify",
};

export function isUserTab(value: unknown): value is UserTab {
  return typeof value === "string" && (USER_TABS as readonly string[]).includes(value);
}

export interface UserSearch {
  tab?: UserTab;
}

/** The route's `validateSearch`: an unknown tab is dropped, and the page shows its first. */
export function parseUserSearch(search: Record<string, unknown>): UserSearch {
  return { tab: isUserTab(search.tab) ? search.tab : undefined };
}

/** What decides which of the optional tabs a customer page has. */
export interface UserTabContext {
  /** A Shopify customer (`shopify:` external id) gets the Shopify tab. */
  isShopifyCustomer: boolean;
  /**
   * The tenant has the `liveBreaks` module (resolved, so off with the storefront too); false while
   * the modules load, so the tab never flashes in.
   */
  liveBreaks: boolean;
}

/** The tabs this user has, in the strip's order. */
export function visibleUserTabs(context: UserTabContext): readonly UserTab[] {
  return USER_TABS.filter(
    (tab) =>
      (tab !== "shopify" || context.isShopifyCustomer) &&
      (tab !== "liveBreaks" || context.liveBreaks),
  );
}

/** The tab to show: the requested one when this user has it, else Purchases. */
export function resolveUserTab(requested: UserTab | undefined, context: UserTabContext): UserTab {
  if (requested && visibleUserTabs(context).includes(requested)) return requested;
  return "purchases";
}
